/**
 * useKeyboardListNav — orchestrator for the SidePanelNotificationList's
 * column-local keyboard navigation. Sits between the headless
 * `useKeyboardNavigation` (low-level: E/J/K/Esc listener, selection
 * state, input-trap guard) and the list's render path. Owns:
 *
 *   - `selectableItems`: flat row-id walk-order derived from the same
 *     view derivations the render path uses. J/K traversal matches
 *     eye-line by construction (insertion-order Map/Set iteration on
 *     ES2015+) — if either side ever resorts, both must move in
 *     lockstep.
 *   - `itemKindById`: tag map so `E` routes to the right callback
 *     (`onDismiss` for notifications, `onDismissConversation` for
 *     conversation threads).
 *   - `columnRootRef`: the outer column wrapper, used as the scope
 *     for `useKeyboardNavigation` AND the focus target on Esc /
 *     summon-with-no-items.
 *   - The `focusSignal` effect: leader-chord "go to top unread" with
 *     race-safe initial-value capture (see comments below) and
 *     defensive selectId fallback so failed-resolve doesn't leave
 *     state diverging from the focused DOM node.
 *   - The scrollIntoView effect: keeps the selected row visible as
 *     J/K traversal moves through the list.
 *
 * Extracted from `SidePanelNotificationList` in PR #411's review
 * follow-up. The focusSignal effect alone went through ~9 rounds of
 * review fixes — splitting it out gives a clear seam for future
 * unit tests and isolates the most-revised code from the rest of
 * the component.
 *
 * Ref-mirror note: like `useAutoMarkRead`, this hook writes its ref
 * mirrors inside `useEffect` (not the render body) so they only run for
 * committed renders — the React 18 concurrent-safe idiom. Migrated
 * codebase-wide in #423.
 */

import type { ConversationThread, DecryptedNotification } from "@reflecto/client-core";
import { type RefObject, useCallback, useEffect, useMemo, useRef } from "react";
import { useKeyboardNavigation } from "../../hooks/useKeyboardNavigation";
import type { AppGroup, TimelineBucket } from "./helpers";
import { findRow } from "./internal";

/**
 * Tagged row identity. Module-scoped so the type isn't re-declared on
 * every render and so consumers (the KbdRow wrapper) can type their
 * `kind` parameter without reaching into the hook's internals.
 */
export type SelectableItem = { kind: "notification" | "conversation" | "call"; id: string };

export interface UseKeyboardListNavOptions {
  /**
   * Whether keyboard navigation is active. When false the hook returns
   * `selectedId: null` and installs no listeners (the underlying
   * `useKeyboardNavigation` early-returns on `enabled: false`).
   */
  enabled: boolean;
  /**
   * Bumped by the host on summon (`Ctrl/Cmd+Shift+K`). The hook keys
   * off the change to select the first unread row. See
   * `SidePanelNotificationListProps.focusSignal` for the full caller
   * contract (incl. the "stable initial value" invariant).
   */
  focusSignal?: number;
  /**
   * Ref to the `<ul>` containing rows. Used by the focusSignal effect
   * (querySelector for `#kbd-row-<id>`) and the scroll effect
   * (querySelector for `[data-msg-id="..."]` / `[data-conv-key="..."]`).
   */
  listRef: RefObject<HTMLUListElement | null>;
  /** Source data for "find first unread" on summon. */
  notifications: DecryptedNotification[];
  /** Source data for "find first unread" on summon. */
  conversations: ConversationThread[];
  /** Renders calls in `selectableItems` first regardless of mode. */
  callNotifications: DecryptedNotification[];
  /** Toggle between the grouped and timeline walk orders. */
  isGrouped: boolean;
  /** Per-app grouping for the grouped walk order. `null` in timeline mode. */
  appGroups: Map<string, AppGroup> | null;
  /** App-package walk order for the grouped mode. */
  allGroupKeys: string[];
  /** Currently-collapsed groups; their rows are skipped in selection. */
  collapsedApps: Set<string>;
  /** Conversation threads in the current (filtered) view. */
  filteredConversations: ConversationThread[];
  /** Date-bucketed walk order for the timeline mode. */
  timelineBuckets: TimelineBucket[];
  onDismiss: (msgId: string) => void;
  onDismissConversation: (key: string) => void;
  /**
   * Optional caller-provided column-root ref. When omitted the hook
   * creates its own. Callers that need to read the ref *before* invoking
   * the hook (e.g. inside a `useCallback` declared earlier in the same
   * component) should declare and forward their own to avoid a forward
   * reference inside that callback.
   */
  columnRootRef?: RefObject<HTMLDivElement | null>;
}

export interface UseKeyboardListNavResult {
  /** Currently-selected row id (null when nothing is selected). */
  selectedId: string | null;
  /**
   * The column-root ref the hook used. Mirrors the caller's `columnRootRef`
   * option when supplied; otherwise the hook's internally-created ref.
   * Callers that pass one in can ignore this field — kept on the result so
   * test harnesses (and any future caller without their own ref) can still
   * attach the wrapper without restructuring.
   */
  columnRootRef: RefObject<HTMLDivElement | null>;
}

export function useKeyboardListNav({
  enabled,
  focusSignal,
  listRef,
  notifications,
  conversations,
  callNotifications,
  isGrouped,
  appGroups,
  allGroupKeys,
  collapsedApps,
  filteredConversations,
  timelineBuckets,
  onDismiss,
  onDismissConversation,
  columnRootRef: externalColumnRootRef,
}: UseKeyboardListNavOptions): UseKeyboardListNavResult {
  // Build the selection order from the same view derivations the
  // render path uses. The keyboard handler walks this flat array so
  // J/K traversal matches the eye-line; `E` routes back to the right
  // callback via `itemKindById`.
  const selectableItems = useMemo<SelectableItem[]>(() => {
    const items: SelectableItem[] = [];
    // Calls render first and are explicitly inserted at the top of
    // the selection order. They CANNOT be double-counted by the
    // timeline branch below because `buildTimelineBuckets` is fed
    // `sorted` (derived from `filteredRegular`), and
    // `regularNotifications` filters out `category === "call"`.
    // Same guarantee for the grouped branch: `appGroups` is
    // `groupByApp(timeSorted)` over the same filtered input.
    for (const n of callNotifications) {
      items.push({ kind: "call", id: n.msgId });
    }
    if (isGrouped && appGroups) {
      // `allGroupKeys` is `union(appGroups.keys(), filteredConversations.
      // map(c => c.packageName))`, so a conversation whose app has zero
      // regular notifications in the current filtered view still gets a
      // group key — and thus its row appears in `selectableItems` and is
      // reachable by J/K. Iteration here relies on `allGroupKeys`'
      // insertion order (Map / Set iteration is insertion-order per
      // ES2015+) matching the render order in the grouped JSX. If
      // `allGroupKeys` is ever resorted (e.g. by unread count), update
      // this traversal in lockstep or J/K will jump out of eye-line.
      for (const appPackage of allGroupKeys) {
        if (collapsedApps.has(appPackage)) continue;
        const groupConvs = filteredConversations.filter((c) => c.packageName === appPackage);
        for (const conv of groupConvs) {
          items.push({ kind: "conversation", id: conv.key });
        }
        const group = appGroups.get(appPackage);
        if (group) {
          for (const n of group.items) items.push({ kind: "notification", id: n.msgId });
        }
      }
    } else {
      for (const bucket of timelineBuckets) {
        for (const item of bucket.items) {
          if (item.kind === "conversation") {
            items.push({ kind: "conversation", id: item.data.key });
          } else {
            items.push({ kind: "notification", id: item.data.msgId });
          }
        }
      }
    }
    return items;
  }, [
    callNotifications,
    isGrouped,
    appGroups,
    allGroupKeys,
    collapsedApps,
    filteredConversations,
    timelineBuckets,
  ]);

  const selectableIds = useMemo(() => selectableItems.map((i) => i.id), [selectableItems]);
  const itemKindById = useMemo(() => {
    const map = new Map<string, SelectableItem["kind"]>();
    for (const item of selectableItems) map.set(item.id, item.kind);
    return map;
  }, [selectableItems]);

  const internalColumnRootRef = useRef<HTMLDivElement>(null);
  const columnRootRef = externalColumnRootRef ?? internalColumnRootRef;

  const handleKeyboardDismiss = useCallback(
    (id: string) => {
      const kind = itemKindById.get(id);
      if (kind === "conversation") onDismissConversation(id);
      // `kind != null` guard: in practice the keydown and selectableItems
      // recompute happen within one render cycle, but if a future
      // refactor breaks that invariant we'd otherwise pass a conversation
      // key to `onDismiss` (which expects a notification msgId). No-op
      // if the id left the selectable set between selection and press.
      else if (kind != null) onDismiss(id);
    },
    [itemKindById, onDismiss, onDismissConversation]
  );

  const { selectedId, selectFirst, selectId } = useKeyboardNavigation({
    ids: selectableIds,
    onDismiss: handleKeyboardDismiss,
    enabled,
    rootRef: columnRootRef,
    // PRD §6.4 / §6.2: on Esc we want to land in
    // COLUMN_VISIBLE_NO_FOCUS. When a row was selected the row had
    // tabIndex={0} and held focus; the row's tabIndex flips to -1
    // when `aria-current` clears, so refocus the column root so the
    // user isn't stranded on a now-untabbable row. When nothing was
    // selected (COLUMN_FOCUSED_NO_SELECT — column root held focus
    // already, or empty inbox), blur to release focus entirely.
    onEscape: (prev) => {
      if (prev != null) {
        columnRootRef.current?.focus();
      } else {
        // Blur whatever held focus (typically the column root
        // itself) so the next Tab starts fresh from page-document
        // order, not from inside the column.
        (document.activeElement as HTMLElement | null)?.blur();
      }
    },
  });

  // Respond to focusSignal — host increments when the leader chord
  // fires. Pick the first unread in render order (PRD §5.7); fall
  // back to the first selectable id; empty inbox focuses the
  // column root so Esc still works (PRD §6.4 / §10.3).
  //
  // `notifications`, `conversations`, and `selectableItems` change on
  // every IndexedDB write — much more often than the user invokes
  // summon. We mirror them through refs so the effect's dep array
  // can be just `[focusSignal, enabled]` and the body reads the
  // current snapshot at summon time. The previous shape included all
  // three in deps, which triggered an effect re-run on every
  // notification change that hit the early-return guard.
  //
  // Seed with the initial prop value, captured at hook-creation time
  // (during the first render, before any state updates fire). This is
  // race-safe: if the SW's `FOCUS_TOP_UNREAD` arrives between mount
  // and the first effect run — which it can, the listener installs
  // synchronously and the SW sends after a 50ms delay — `focusSignal`
  // is already `1` when the effect runs, but the ref still holds the
  // captured `0`, so the inequality fires the summon.
  //
  // Invariant for callers: pass `focusSignal` from first render with
  // a stable initial value (typically `useState(0)`). A caller that
  // omits `focusSignal` on mount and adds it later WILL fire a
  // spurious summon on the prop-add — this is intentional given the
  // race-safety requirement; document it at the caller if relevant.
  const lastFocusSignalRef = useRef<number | undefined>(focusSignal);
  // Ref mirrors written inside useEffect (not the render body) so they
  // only run for committed renders — React 18 concurrent-safe (see #423).
  const focusNotificationsRef = useRef(notifications);
  const focusConversationsRef = useRef(conversations);
  const selectableItemsRef = useRef(selectableItems);
  const selectFirstRef = useRef(selectFirst);
  const selectIdRef = useRef(selectId);
  useEffect(() => {
    focusNotificationsRef.current = notifications;
    focusConversationsRef.current = conversations;
    selectableItemsRef.current = selectableItems;
    selectFirstRef.current = selectFirst;
    selectIdRef.current = selectId;
  });
  useEffect(() => {
    if (!enabled) return;
    // Optional-prop caller: nothing to summon to. Record and bail.
    if (focusSignal == null) {
      lastFocusSignalRef.current = focusSignal;
      return;
    }
    if (focusSignal === lastFocusSignalRef.current) return;
    lastFocusSignalRef.current = focusSignal;
    const unreadIds = new Set<string>(
      focusNotificationsRef.current.filter((n) => n.isRead === false).map((n) => n.msgId)
    );
    const unreadConvKeys = new Set<string>(
      focusConversationsRef.current.filter((c) => c.unreadCount > 0).map((c) => c.key)
    );
    const items = selectableItemsRef.current;
    const firstUnread = items.find((item) =>
      item.kind === "conversation" ? unreadConvKeys.has(item.id) : unreadIds.has(item.id)
    );
    // Try the first unread; fall through to first selectable if the
    // id isn't in the current `ids` (defensive — a race between the
    // reads here and the hook's idsRef shouldn't manifest, but if it
    // does, `selectId` returning `false` makes us select *something*
    // rather than silently no-op'ing into "nothing happened" UX).
    // Track which id actually got selected so the focus target
    // below matches it. If `selectId(firstUnread.id)` succeeds the
    // target is `firstUnread`; if the defensive fallback fires the
    // target is `items[0]`. Without this split, a failed selectId
    // would leave `targetId === firstUnread.id` (no matching DOM
    // node) while `aria-current` was actually applied to `items[0]`
    // — visual/AT mismatch for one frame.
    let resolvedId: string | undefined;
    if (firstUnread && selectIdRef.current(firstUnread.id)) {
      resolvedId = firstUnread.id;
    } else if (items.length > 0) {
      selectFirstRef.current();
      resolvedId = items[0].id;
    }
    // Roving tabindex: focus the selected row directly so screen
    // readers announce it (and so the row's tabIndex={0} is
    // exercised). Re-deriving rather than reading `selectedId` from
    // the hook's return because the latter is one render behind the
    // `selectId` we just invoked.
    // Empty inbox: fall back to focusing the column root so Esc
    // still works (PRD §6.4 / §10.3).
    const targetId = resolvedId;
    // `CSS.escape` is the right helper here because `#kbd-row-${id}` is
    // an *ID selector* (CSS identifier context). The scroll-effect
    // below uses a custom `esc()` because it targets attribute-value
    // selectors, which need different escaping rules.
    const row = targetId
      ? listRef.current?.querySelector<HTMLElement>(`#kbd-row-${CSS.escape(targetId)}`)
      : null;
    // `preventScroll: true` so we don't scroll-on-focus here AND
    // then again from the `scrollIntoView` effect below — that
    // effect uses `behavior: "instant"` and `block: "nearest"` to
    // honour the PRD's J/K snappy-nav requirement, while the
    // browser's default focus-scroll behaviour can animate.
    (row ?? columnRootRef.current)?.focus({ preventScroll: true });
    // listRef is intentionally excluded — useRef returns a stable object
    // whose identity never changes; including it would be a no-op dep
    // that misleads readers and could re-fire the effect if a caller
    // ever passed createRef() instead of useRef() (advancing
    // lastFocusSignalRef unexpectedly).
  }, [focusSignal, enabled, columnRootRef.current, listRef.current?.querySelector]);

  // Scroll the selected row into view when selection moves. findRow resolves it
  // (data-msg-id / data-conv-key / #kbd-row-<id>); the [aria-current] fallback is a
  // selection last-resort if even #kbd-row is absent.
  useEffect(() => {
    if (!enabled || selectedId == null) return;
    const list = listRef.current;
    if (!list) return;
    const row =
      findRow(list, selectedId) ?? list.querySelector<HTMLElement>(`[aria-current="true"]`);
    // `behavior: "instant"` per PRD §5.7 — J/K presses that animate
    // per-step feel laggy when held; instant scroll keeps the
    // keyboard nav snappy.
    row?.scrollIntoView({ block: "nearest", behavior: "instant" });
    // listRef is intentionally excluded — stable RefObject identity; same
    // reasoning as the focusSignal effect above.
  }, [selectedId, enabled, listRef.current]);

  return { selectedId, columnRootRef };
}
